import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, RefreshCw, User, Building2, PieChart } from "lucide-react";

const CATEGORY_LABELS = { comida: "Comida", transporte: "Transporte", hospedaje: "Hospedaje", otros: "Otros" };

function money(n) {
  return `$${(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
}

export default function OpsViaticosReportes() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      const res = await api.get(`/ops/viaticos/reports/aggregate?${params.toString()}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="ops-viaticos-reportes-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/ops/viaticos")} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Reportes de viaticos</h1>
          <p className="text-sm text-slate-500">Agregado por persona, sucursal y categoria de gasto</p>
        </div>
      </div>

      <div className="flex gap-3 items-end bg-white rounded-xl border border-slate-200 p-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Desde</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" data-testid="reportes-from-date" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Hasta</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" data-testid="reportes-to-date" />
        </div>
        <button onClick={fetchData} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium">
          Filtrar
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-4 gap-3">
            {Object.entries(data.by_category).map(([cat, amt]) => (
              <div key={cat} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`reportes-category-${cat}`}>
                <div className="text-xs text-slate-500 mb-1">{CATEGORY_LABELS[cat] || cat}</div>
                <div className="font-bold text-lg text-slate-900">{money(amt)}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><User className="w-4 h-4" /> Por persona</h3>
            {data.by_person.length === 0 ? <p className="text-sm text-slate-400">Sin datos en el rango seleccionado.</p> : (
              <div className="space-y-2">
                {data.by_person.map(p => (
                  <div key={p.requester_email} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg text-sm" data-testid={`reportes-person-${p.requester_email}`}>
                    <span className="font-medium text-slate-800">{p.requester_email} <span className="text-slate-400">({p.count})</span></span>
                    <span className="flex gap-4">
                      <span className="text-slate-500">Solicitado: <b className="text-slate-800">{money(p.total_requested)}</b></span>
                      <span className="text-slate-500">Gastado: <b className="text-slate-800">{money(p.total_spent)}</b></span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4" /> Por sucursal</h3>
            {data.by_branch.length === 0 ? <p className="text-sm text-slate-400">Sin datos en el rango seleccionado.</p> : (
              <div className="space-y-2">
                {data.by_branch.map(b => (
                  <div key={b.branch_id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg text-sm" data-testid={`reportes-branch-${b.branch_id}`}>
                    <span className="font-medium text-slate-800">{b.branch_name} <span className="text-slate-400">({b.count})</span></span>
                    <span className="flex gap-4">
                      <span className="text-slate-500">Solicitado: <b className="text-slate-800">{money(b.total_requested)}</b></span>
                      <span className="text-slate-500">Gastado: <b className="text-slate-800">{money(b.total_spent)}</b></span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
